// First-run onboarding (docs/07 §7.4) — a guided coachmark tour that spotlights
// the real UI in sequence and walks a newcomer through making their first loop.
// Re-openable via "How it works" in Settings. Shows once (a localStorage flag).

import { createTour, type Tour, type TourStep } from "./tour.ts";

const SEEN_KEY = "loopfield:onboarded";

const STEPS: readonly TourStep[] = [
  {
    title: "Let's make a loop together",
    body: "Loopfield turns short takes into a song that plays in time. We'll build your first loop in four quick steps. Ready?",
  },
  {
    target: ".palette",
    title: "1 · Pick an instrument",
    body: "Everything down here is playable. Drums to start; melodic instruments are locked to a scale so you can't hit a wrong note.",
    action: {
      cue: "Click any instrument to select it.",
      success: "Great pick.",
      on: { selector: ".chip-inst", event: "click" },
    },
  },
  {
    target: ".pads",
    title: "2 · Play a sound",
    body: "Tap a pad, or press the key shown on it (drums: Z X C V B N). Give it a try.",
    action: {
      cue: "Tap a pad to hear it.",
      success: "That's the sound. Feels good, right?",
      on: { selector: ".pad-btn", event: "pointerdown" },
    },
  },
  {
    target: ".iconbtn--rec",
    title: "3 · Record your first loop",
    body: "Press Record (or R). You'll get a 3-2-1 count-in, then play a short pattern. When the bar comes around, it starts looping on its own.",
    action: {
      cue: "Hit Record, then play a bar.",
      success: "Boom. Your first loop is looping.",
      appear: ".lane",
    },
  },
  {
    target: ".lanes",
    title: "4 · Stack and shape",
    body: "There's your loop. Pick another instrument and record again to layer on top, in sync. Drag a lane to reorder; per layer you can mute, solo, set volume, lock, duplicate, or delete.",
  },
  {
    target: ".topbar__title",
    title: "Name your session",
    body: "Click the title to rename it. Everything autosaves to its own link, so you can always come back.",
  },
  {
    target: ".topbar__tools",
    title: "That's it. You're a looper now.",
    body: "Settings holds the mix, export (WAV/MP3 or stems), and a share link. Handy keys: Space = play, R = record, Cmd/Ctrl+Z = undo. Go make something.",
  },
];

export interface Onboarding {
  element: HTMLElement;
  open(): void;
  maybeAutoOpen(): void;
}

export function createOnboarding(): Onboarding {
  const tour: Tour = createTour(STEPS);

  const markSeen = (): void => {
    try {
      localStorage.setItem(SEEN_KEY, "1");
    } catch {
      // ignore
    }
  };

  return {
    element: tour.element,
    open() {
      tour.start();
    },
    maybeAutoOpen() {
      let seen = false;
      try {
        seen = localStorage.getItem(SEEN_KEY) === "1";
      } catch {
        seen = false;
      }
      if (!seen) {
        markSeen();
        tour.start();
      }
    },
  };
}
